/**
 * 계정 이전 원장 조회 클라이언트 — 활성 verse(출발 측)의 `migrationRequests` 를 읽는다.
 *
 * 활성 verse 의 `migrationRequests` 컬렉션(= 계정 이전 큐)을 어드민에서
 * 읽기 전용으로 조회한다. 전용 서버 RPC 를 추가하지 않고 Query Console 이 쓰는 범용
 * `admin.queryGet` / `admin.queryCount` 를 재사용한다 — 서버 배포 없이 어드민만
 * 배포하면 바로 동작시키기 위함.
 *
 * 대가: `queryGet` 에 필드 프로젝션이 없어 레코드 무게의 대부분인 `snapshot`
 * (유저 state 전체 + 보유 자산 전부)이 와이어를 그대로 탄다. 그래서
 *  - 기본 페이지 크기를 20 으로 낮추고(Query Console 기본 50),
 *  - 수신 즉시 `toLedgerRow` 로 경량 row 를 만들어 snapshot 참조를 버린다(화면
 *    상태에 스냅샷을 담지 않는다).
 * 어차피 스냅샷이 오므로 nickname 만 꺼내 쓴다 — DONE 건은 출발 계정의 라이브 state 가
 * 리셋되므로(finalizeMigrationSource) 다른 경로로는 닉네임을 얻을 수 없다.
 *
 * 나중에 페이로드가 문제가 되면 서버에 경량 전용 RPC 를 추가하고 이 파일만
 * 갈아끼우면 된다 — UI 는 이 모듈의 시그니처에만 의존한다.
 */

import type { AdminQueryGetInput } from '@rb/shared/admin-query.types';
import { isEthereumAddress, toChecksumAddress } from '@rb/shared/eip55';
import type { MigrationRequestState } from '@rb/shared/migration/types';
import { adminQueryCount, adminQueryGet } from '@/client/admin.client';

export const MIGRATION_REQUESTS_COLLECTION = 'migrationRequests';

export const MIGRATION_STATES: readonly MigrationRequestState[] = [
  'QUEUED',
  'PROCESSING',
  'DONE',
  'REJECTED',
] as const;

export type MigrationStateFilter = MigrationRequestState | 'ALL';

/** 스냅샷을 벗긴 원장 한 줄. 화면이 쓰는 유일한 형태. */
export interface MigrationLedgerRow {
  migrationId: string;
  /** 출발 계정. */
  P: string;
  /** 도착 계정. */
  A: string;
  state: MigrationRequestState;
  /** REJECTED 사유. */
  reason?: string;
  createdAt: number;
  /** 브리지 클레임 시각. */
  claimedAt?: number;
  /** 스냅샷에서 꺼낸 표시용 닉네임. */
  nickname?: string;
}

export interface MigrationStateCounts {
  total: number;
  QUEUED: number;
  PROCESSING: number;
  DONE: number;
  REJECTED: number;
}

export interface MigrationPageResult {
  rows: MigrationLedgerRow[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** 기본 페이지 크기 — snapshot 이 통째로 실려오므로 의도적으로 작게 잡는다. */
export const DEFAULT_PAGE_SIZE = 20;

/** 주소 검색 시 표기당 최대 조회 건수(계정당 레코드는 소수라 넉넉하다). */
const SEARCH_LIMIT = 50;

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** snapshot.state.nickname 만 추출 — 나머지 스냅샷은 버린다. */
function extractNickname(snapshot: unknown): string | undefined {
  if (!snapshot || typeof snapshot !== 'object') return undefined;
  const state = (snapshot as { state?: unknown }).state;
  if (!state || typeof state !== 'object') return undefined;
  return asString((state as { nickname?: unknown }).nickname);
}

/** 원시 `migrationRequests` 레코드 → 경량 row. */
export function toLedgerRow(raw: Record<string, unknown> & { __id: string }): MigrationLedgerRow {
  const claimedAt = asNumber(raw.claimedAt);
  const reason = asString(raw.reason);
  const nickname = extractNickname(raw.snapshot);
  return {
    migrationId: asString(raw.migrationId) ?? raw.__id,
    P: asString(raw.P) ?? '',
    A: asString(raw.A) ?? '',
    state: (asString(raw.state) ?? 'QUEUED') as MigrationRequestState,
    createdAt: asNumber(raw.createdAt) ?? 0,
    ...(claimedAt !== undefined ? { claimedAt } : {}),
    ...(reason !== undefined ? { reason } : {}),
    ...(nickname !== undefined ? { nickname } : {}),
  };
}

/** 상태 필터 → queryGet 입력. 정렬은 항상 최신순. */
export function buildLedgerQuery(opts: {
  state: MigrationStateFilter;
  limit: number;
  cursor: string | number | null;
}): AdminQueryGetInput {
  const input: AdminQueryGetInput = {
    collection: MIGRATION_REQUESTS_COLLECTION,
    orderBy: { field: 'createdAt', direction: 'desc' },
    limit: opts.limit,
  };
  if (opts.state !== 'ALL') {
    input.filters = [{ field: 'state', operator: '==', value: opts.state }];
  }
  if (opts.cursor !== null && opts.cursor !== undefined && opts.cursor !== '') {
    input.cursor = opts.cursor;
  }
  return input;
}

/**
 * 검색어의 저장 표기 후보들. `P` 는 서버가 `$sender.account` 를 원본 그대로 넣고
 * `A` 는 EIP-55 체크섬으로 정규화해 넣으므로, 한 표기만으로는 반쪽만 걸린다.
 */
export function addressCandidates(input: string): string[] {
  const trimmed = input.trim();
  if (!trimmed) return [];
  if (!isEthereumAddress(trimmed)) return [trimmed];
  return Array.from(new Set([trimmed, trimmed.toLowerCase(), toChecksumAddress(trimmed)]));
}

export async function fetchMigrationPage(opts: {
  state: MigrationStateFilter;
  limit: number;
  cursor: string | number | null;
}): Promise<MigrationPageResult> {
  const page = await adminQueryGet(buildLedgerQuery(opts));
  return {
    rows: page.records.map(toLedgerRow),
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
  };
}

/**
 * 특정 주소가 소스(P) 또는 대상(A) 인 레코드를 모두 찾는다. 표기 후보 × 두 필드만큼
 * 질의하므로 최대 6회 — V8 은 **함수명당** 초당 10회(sliding window) 제한이라
 * `Promise.all` 로 몰아 던지지 않고 순차로 돈다(rpc-client.ts 규약).
 */
export async function searchMigrationsByAddress(address: string): Promise<MigrationLedgerRow[]> {
  const candidates = addressCandidates(address);
  if (candidates.length === 0) return [];

  const byId = new Map<string, MigrationLedgerRow>();
  for (const field of ['P', 'A'] as const) {
    for (const value of candidates) {
      const page = await adminQueryGet({
        collection: MIGRATION_REQUESTS_COLLECTION,
        filters: [{ field, operator: '==', value }],
        limit: SEARCH_LIMIT,
      });
      for (const raw of page.records) {
        const row = toLedgerRow(raw);
        // 같은 레코드가 표기 후보/필드를 넘나들며 중복 등장한다 — migrationId 로 접는다.
        if (!byId.has(row.migrationId)) byId.set(row.migrationId, row);
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
}

/** 전체 + 상태별 건수. 같은 함수명 5회라 반드시 순차 호출한다. */
export async function fetchMigrationCounts(): Promise<MigrationStateCounts> {
  const total = await adminQueryCount({ collection: MIGRATION_REQUESTS_COLLECTION });
  const counts: MigrationStateCounts = {
    total,
    QUEUED: 0,
    PROCESSING: 0,
    DONE: 0,
    REJECTED: 0,
  };
  for (const state of MIGRATION_STATES) {
    counts[state] = await adminQueryCount({
      collection: MIGRATION_REQUESTS_COLLECTION,
      filters: [{ field: 'state', operator: '==', value: state }],
    });
  }
  return counts;
}
